import {
  CHANNEL_TYPE_LABELS,
  CONTENT_FORMAT_LABELS,
  CONTENT_STATUS_HELP,
  CONTENT_STATUS_LABELS,
  CONTENT_STATUS_LONG_LABELS,
  canTransition,
  compareBoardOrder,
  formatNumber,
  type ContentItemDto,
  type ContentStatus,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { useState } from 'react';
import { CompanyMark } from '../../components/CompanyMark.js';
import { AgentMark, DemoMark, MetaChip } from '../../components/StatusBadge.js';
import { api } from '../../lib/api.js';
import { useAreaColor } from '../../lib/area-color.js';
import { cn } from '../../lib/cn.js';
import { deadlineFor } from './ContentRow.js';

/** The board only shows the live pipeline; rejected/cancelled live in the list. */
const COLUMNS: ContentStatus[] = ['SUGGESTED', 'APPROVED', 'DRAFT', 'SCHEDULED', 'PUBLISHED'];

const COLUMN_TONE: Record<string, string> = {
  SUGGESTED: 'text-violet',
  APPROVED: 'text-accent',
  DRAFT: 'text-ink-soft',
  SCHEDULED: 'text-warning',
  PUBLISHED: 'text-success',
};

/**
 * Board view with drag-and-drop. A card can only be dropped where the status
 * machine allows it — the same rules the API enforces (packages/shared).
 * Transitions that need extra input (reject, schedule) open the drawer instead.
 */
/** Retired statuses gathered into the hidden «Utgått» column. */
const RETIRED_STATUSES: ContentStatus[] = ['EXPIRED', 'REJECTED', 'CANCELLED'];

export function KanbanBoard({
  items,
  onOpen,
  onMove,
  onReorder,
  filter,
  flashId,
}: {
  items: ContentItemDto[];
  onOpen: (item: ContentItemDto) => void;
  onMove: (item: ContentItemDto, to: ContentStatus) => void;
  /** Persist a manual order within one status column (ids in the wanted order). */
  onReorder: (status: ContentStatus, orderedIds: string[]) => void;
  /** The active company/area filter, so «Utgått» matches the rest of the board. */
  filter?: { company?: string; areaId?: string };
  /** A card to flash briefly — set right after its editor closes. */
  flashId?: string | null;
}) {
  const [dragging, setDragging] = useState<ContentItemDto | null>(null);
  const [hovered, setHovered] = useState<ContentStatus | null>(null);
  // Where a within-column drop would land: a card id and whether it drops after it.
  const [dropTarget, setDropTarget] = useState<{ id: string; after: boolean } | null>(null);
  const [showExpired, setShowExpired] = useState(false);

  // Drop the dragged card next to `targetId` within its own column and persist
  // the new order. A move to a different column is a status change, handled by
  // the column's own onDrop (onMove) — not here.
  function reorderWithin(columnItems: ContentItemDto[], targetId: string, after: boolean) {
    if (!dragging) return;
    const ids = columnItems.map((it) => it.id);
    const without = ids.filter((id) => id !== dragging.id);
    const idx = without.indexOf(targetId);
    if (idx === -1) return;
    without.splice(after ? idx + 1 : idx, 0, dragging.id);
    if (ids.length === without.length && ids.every((id, i) => id === without[i])) return;
    onReorder(dragging.status, without);
  }

  // «Utgått» is fetched only when revealed — it can grow large over time.
  const { data: expired } = useQuery({
    queryKey: ['content', 'retired', filter?.company, filter?.areaId],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filter?.company && filter.company !== 'ALLE') params.set('company', filter.company);
      if (filter?.areaId) params.set('areaId', filter.areaId);
      params.set('take', '100');
      for (const status of RETIRED_STATUSES) params.append('status', status);
      return api.get<{ items: ContentItemDto[] }>(`/content?${params.toString()}`);
    },
    enabled: showExpired,
  });

  return (
    <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
      {COLUMNS.map((status) => {
        // The board is the one view that honours manual order (boardOrder);
        // unplaced cards keep the incoming date order and fall to the end.
        const columnItems = items.filter((item) => item.status === status).sort(compareBoardOrder);
        const droppable = dragging ? canTransition(dragging.status, status) : false;
        // A drag that started in this column reorders within it rather than
        // changing status.
        const reordering = Boolean(dragging && dragging.status === status);

        return (
          <section
            key={status}
            data-testid="kanban-column"
            data-status={status}
            onDragOver={(event) => {
              if (!droppable) return;
              event.preventDefault();
              setHovered(status);
            }}
            onDragLeave={() => setHovered((current) => (current === status ? null : current))}
            onDrop={(event) => {
              event.preventDefault();
              setHovered(null);
              if (dragging && droppable) onMove(dragging, status);
              setDragging(null);
              setDropTarget(null);
            }}
            className={cn(
              'rounded-panel bg-panel/40 flex w-[356px] shrink-0 flex-col border transition',
              hovered === status && droppable
                ? 'border-accent/50 bg-accent/[0.06]'
                : dragging && !droppable
                  ? 'border-hairline opacity-40'
                  : 'border-hairline',
            )}
          >
            <header className="border-hairline border-b px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className={cn('label-micro', COLUMN_TONE[status])}>
                  {CONTENT_STATUS_LONG_LABELS[status]}
                </span>
                <span className="num-tabular text-ink-soft text-xs">
                  {formatNumber(columnItems.length)}
                </span>
              </div>
              {/* What is waiting here, so nobody has to guess what the column means. */}
              <p className="text-ink-soft/70 mt-1 text-[11px] leading-snug">
                {CONTENT_STATUS_HELP[status]}
              </p>
            </header>

            <div className="min-h-[120px] flex-1 space-y-2 p-2">
              {columnItems.map((item) => (
                <BoardCard
                  key={item.id}
                  item={item}
                  onOpen={onOpen}
                  flash={flashId === item.id}
                  isDragging={dragging?.id === item.id}
                  reorderable={reordering && dragging?.id !== item.id}
                  dropHint={
                    dropTarget?.id === item.id ? (dropTarget.after ? 'after' : 'before') : null
                  }
                  onReorderOver={(after) => setDropTarget({ id: item.id, after })}
                  onReorderDrop={(after) => {
                    reorderWithin(columnItems, item.id, after);
                    setDragging(null);
                    setHovered(null);
                    setDropTarget(null);
                  }}
                  onDragStart={() => setDragging(item)}
                  onDragEnd={() => {
                    setDragging(null);
                    setHovered(null);
                    setDropTarget(null);
                  }}
                />
              ))}
              {columnItems.length === 0 && (
                <p className="text-ink-soft/60 px-2 py-6 text-center text-[11px]">Tomt</p>
              )}
            </div>
          </section>
        );
      })}

      {/* «Utgått» is hidden by default — a narrow strip reveals it. It gathers the
          retired statuses (utgått, avvist, kansellert); cards can be dragged back
          to Forslag to reopen them. */}
      {showExpired ? (
        <section
          data-testid="kanban-column"
          data-status="EXPIRED"
          className="rounded-panel border-hairline bg-panel/40 flex w-[356px] shrink-0 flex-col border"
        >
          <header className="border-hairline flex items-start justify-between gap-2 border-b px-3 py-2.5">
            <div>
              <span className="label-micro text-ink-soft">Utgått</span>
              <p className="text-ink-soft/70 mt-1 text-[11px] leading-snug">
                Ikke lenger aktuelt — utgåtte forslag, avviste og kansellerte saker.
              </p>
            </div>
            <button
              type="button"
              aria-label="Skjul utgått"
              onClick={() => setShowExpired(false)}
              className="text-ink-soft hover:text-ink shrink-0"
            >
              <ChevronRight size={15} aria-hidden />
            </button>
          </header>
          <div className="min-h-[120px] flex-1 space-y-2 p-2">
            {(expired?.items ?? []).map((item) => (
              <BoardCard
                key={item.id}
                item={item}
                retired
                onOpen={onOpen}
                onDragStart={() => setDragging(item)}
                onDragEnd={() => {
                  setDragging(null);
                  setHovered(null);
                  setDropTarget(null);
                }}
              />
            ))}
            {(expired?.items ?? []).length === 0 && (
              <p className="text-ink-soft/60 px-2 py-6 text-center text-[11px]">Ingenting utgått</p>
            )}
          </div>
        </section>
      ) : (
        <button
          type="button"
          onClick={() => setShowExpired(true)}
          className="rounded-panel border-hairline bg-panel/40 text-ink-soft hover:text-ink hover:border-accent/30 flex w-9 shrink-0 flex-col items-center gap-2 border py-3 transition"
          title="Vis utgåtte, avviste og kansellerte saker"
        >
          <ChevronLeft size={15} aria-hidden />
          <span className="text-[11px] [writing-mode:vertical-rl]">Vis utgått</span>
        </button>
      )}
    </div>
  );
}

function BoardCard({
  item,
  onOpen,
  onDragStart,
  onDragEnd,
  isDragging = false,
  reorderable = false,
  dropHint = null,
  onReorderOver,
  onReorderDrop,
  retired = false,
  flash = false,
}: {
  item: ContentItemDto;
  onOpen: (item: ContentItemDto) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  /** This card is the one currently being dragged. */
  isDragging?: boolean;
  /** Briefly highlight this card — it is the one the editor just closed on. */
  flash?: boolean;
  /** A same-column drag is in progress and this card can be dropped onto. */
  reorderable?: boolean;
  /** Draw a drop line above («before») or below («after») this card. */
  dropHint?: 'before' | 'after' | null;
  onReorderOver?: (after: boolean) => void;
  onReorderDrop?: (after: boolean) => void;
  /** In the «Utgått» column, show which retired status this is. */
  retired?: boolean;
}) {
  const areaColor = useAreaColor();
  const reduceMotion = useReducedMotion();
  const deadline = deadlineFor(item);

  // The flash: an animated ring pulse, or a steady ring when the viewer prefers
  // reduced motion. Inset box-shadow, so it never shifts layout or is clipped.
  const flashClass = flash
    ? reduceMotion
      ? 'shadow-[inset_0_0_0_2px_rgb(var(--nova-accent)/0.6)]'
      : 'animate-card-flash'
    : '';

  // Whether a hovered drop would land after this card: below its vertical middle.
  const dropsAfter = (event: { currentTarget: HTMLElement; clientY: number }) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientY > rect.top + rect.height / 2;
  };

  const targets = item.channelTargets ?? [];
  const publishedChannels = targets.filter((t) => t.status === 'PUBLISHED_CONFIRMED').length;

  // The channels this card targets, by name (or the channel type where a name is
  // missing). Keep the card tidy: show the first couple, then «+N».
  const channelNames = targets
    .map((t) => t.channel?.name ?? (t.channel?.type ? CHANNEL_TYPE_LABELS[t.channel.type] : null))
    .filter((name): name is string => Boolean(name));
  const shownChannels = channelNames.slice(0, 2);
  const extraChannels = channelNames.length - shownChannels.length;

  return (
    <article
      draggable
      data-testid="board-card"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={
        reorderable
          ? (event) => {
              // Let the card accept the drop (the column would otherwise reject a
              // same-status move) and show where it would land.
              event.preventDefault();
              event.stopPropagation();
              onReorderOver?.(dropsAfter(event));
            }
          : undefined
      }
      onDrop={
        reorderable
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              onReorderDrop?.(dropsAfter(event));
            }
          : undefined
      }
      onClick={() => onOpen(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(item);
        }
      }}
      tabIndex={0}
      role="button"
      className={cn(
        'border-hairline bg-base/50 relative cursor-grab overflow-hidden rounded-lg border p-3 pl-4 transition active:cursor-grabbing',
        'hover:border-accent/30 hover:bg-raised/50',
        item.isOverdue && 'border-danger/30',
        isDragging && 'opacity-40',
        dropHint === 'before' &&
          'before:bg-accent before:absolute before:inset-x-2 before:top-0 before:h-0.5 before:rounded-full',
        dropHint === 'after' &&
          'after:bg-accent after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full',
        flashClass,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: areaColor(item.area?.color) }}
      />
      <p className="text-ink line-clamp-3 text-[13px] leading-snug">{item.title}</p>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {retired && <MetaChip>{CONTENT_STATUS_LABELS[item.status]}</MetaChip>}
        {item.area && <MetaChip color={item.area.color}>{item.area.name}</MetaChip>}
        <MetaChip>{CONTENT_FORMAT_LABELS[item.format]}</MetaChip>
        {shownChannels.map((name) => (
          <MetaChip key={name}>{name}</MetaChip>
        ))}
        {extraChannels > 0 && <MetaChip className="text-ink-soft/80">+{extraChannels}</MetaChip>}
        {publishedChannels > 0 && (
          <span
            className="border-hairline bg-raised/60 text-ink-soft inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]"
            title={`${publishedChannels} av ${targets.length} kanaler publisert`}
          >
            <Layers size={10} aria-hidden />
            {publishedChannels}/{targets.length} publisert
          </span>
        )}
        {item.source === 'AGENT' && <AgentMark />}
        {item.isSeed && <DemoMark />}
      </div>

      {deadline && (
        <p
          className={cn(
            'mt-2 pr-10 text-[11px]',
            deadline.tone === 'danger'
              ? 'text-danger'
              : deadline.tone === 'warning'
                ? 'text-warning'
                : 'text-ink-soft',
          )}
        >
          {deadline.label}
        </p>
      )}

      <span className="pointer-events-none absolute bottom-2 right-2.5">
        <CompanyMark company={item.area?.company} />
      </span>
    </article>
  );
}
